// Argus — AgentCard: card individual în sidebar cu thumbnail + status + DayJS time + quick actions.

import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Tooltip,
  IconButton,
  Stack,
  Badge,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import VideocamIcon from '@mui/icons-material/Videocam';
import ComputerIcon from '@mui/icons-material/Computer';
import type { Agent } from '../../lib/api';
import { dayjs } from '../../lib/dayjsSetup';
import { useAgentVersionStatus } from '../../lib/agentVersion';
import { VersionLink } from '../shared/VersionLink';
import { AgentVersionModal } from '../dashboard/AgentVersionModal';

const THUMBNAIL_REFRESH_MS = 60_000;

export interface AgentCardProps {
  agent: Agent;
  selected: boolean;
  bulkSelected: boolean;
  onClick: () => void;
  onToggleBulk: () => void;
  onQuickScreenshot: () => void;
  onQuickFileExplorer: () => void;
  onQuickLivestream: () => void;
}

export function AgentCard({
  agent,
  selected,
  bulkSelected,
  onClick,
  onToggleBulk,
  onQuickScreenshot,
  onQuickFileExplorer,
  onQuickLivestream,
}: AgentCardProps): JSX.Element {
  const [thumbnailVersion, setThumbnailVersion] = useState(() => Date.now());
  const [thumbnailBlobUrl, setThumbnailBlobUrl] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);

  // Auto-refresh thumbnail la 1 min pentru online agents (fallback REST GET dacă WS push lipsește
  // — e.g. la primă montare card, înainte ca push-ul periodic 60s al agentului să sosească).
  useEffect(() => {
    if (!agent.isOnline) return;
    const interval = setInterval(() => setThumbnailVersion(Date.now()), THUMBNAIL_REFRESH_MS);
    return () => clearInterval(interval);
  }, [agent.isOnline]);

  // Bloc desktop-thumbnail-ws 2026-05-06: WS push inline PNG → decode base64 → Blob URL.
  // + agent-intervals 2026-05-06: lastThumbnailLocalAt = timestamp local când a sosit ultimul push,
  // folosit pentru CircularProgress countdown overlay până la următorul tick.
  const [lastThumbnailLocalAt, setLastThumbnailLocalAt] = useState<number>(() =>
    agent.lastThumbnailAt ? new Date(agent.lastThumbnailAt).getTime() : Date.now() - agent.thumbnailIntervalSeconds * 1000,
  );
  useEffect(() => {
    const handler = (ev: Event) => {
      const ce = ev as CustomEvent<{ agentId?: string; pngBase64?: string }>;
      if (ce.detail?.agentId !== agent.id || !ce.detail?.pngBase64) return;
      try {
        const binary = atob(ce.detail.pngBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        setThumbnailBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setLastThumbnailLocalAt(Date.now());
      } catch { /* ignore decode error */ }
    };
    window.addEventListener('argus:agent-thumbnail', handler);
    return () => window.removeEventListener('argus:agent-thumbnail', handler);
  }, [agent.id]);

  // Tick local 1Hz pentru a re-render countdown CircularProgress (NU re-render parent — doar local state).
  const [tickNow, setTickNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!agent.isOnline) return;
    const handle = window.setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, [agent.isOnline]);
  const intervalMs = agent.thumbnailIntervalSeconds * 1000;
  const elapsedMs = Math.max(0, tickNow - lastThumbnailLocalAt);
  const progressPct = Math.min(100, (elapsedMs / intervalMs) * 100);
  const remainingSec = Math.max(0, Math.ceil((intervalMs - elapsedMs) / 1000));

  // Cleanup blob URL la unmount.
  useEffect(() => () => {
    if (thumbnailBlobUrl) URL.revokeObjectURL(thumbnailBlobUrl);
  }, [thumbnailBlobUrl]);

  const thumbnailSrc = thumbnailBlobUrl ?? `/api/v1/agents/${agent.id}/thumbnail?v=${thumbnailVersion}`;
  const displayName = agent.pcName || agent.hostname;
  const userName = agent.windowsUsername || agent.lastSeenUser;
  const machineLabel = userName ? `\\\\${displayName}\\${userName}` : `\\\\${displayName}`;
  // Pentru "Online for X" folosim lastConnectedAt (moment connect WS), NU lastHeartbeatAt
  // care e refresh-uit la fiecare 30s și ar afișa mereu "a few seconds".
  const lastSeenAt = agent.lastHeartbeatAt;
  const onlineSinceAt = agent.lastConnectedAt ?? lastSeenAt;
  const relativeText = agent.isOnline
    ? onlineSinceAt
      ? `Online for ${dayjs(onlineSinceAt).fromNow(true)}`
      : 'Online'
    : lastSeenAt
      ? `Last seen ${dayjs(lastSeenAt).fromNow()}`
    : 'No data';

  // VersionLink + modal — identice cu AgentDashboard title. Click pe (vX.Y.Z) deschide modal.
  const versionStatus = useAgentVersionStatus(agent.id);
  const [versionModalOpen, setVersionModalOpen] = useState(false);

  const tooltipTitle = (
    <Stack spacing={0.25} sx={{ fontSize: 11 }}>
      <span><b>ID:</b> {agent.id.substring(0, 8)}…{agent.id.substring(24)}</span>
      <span><b>Hostname:</b> {agent.hostname}</span>
      <span><b>Agent ver:</b> {agent.agentVersion ?? '—'}</span>
      <span><b>Last heartbeat:</b> {lastSeenAt ? dayjs(lastSeenAt).format('YYYY-MM-DD HH:mm:ss') : '—'}</span>
      {agent.lastMetrics ? (
        <span><b>Metrics:</b> CPU {agent.lastMetrics.cpuPct}% / RAM {(agent.lastMetrics.ramUsedMb / 1024).toFixed(1)} GB / Disk {agent.lastMetrics.diskFreeGb} GB</span>
      ) : null}
    </Stack>
  );

  return (
    <>
    <Tooltip title={tooltipTitle} placement="right" arrow>
      <Card
        sx={{
          mb: 1,
          backgroundColor: selected ? 'action.selected' : 'background.paper',
          border: selected ? '1px solid' : '1px solid transparent',
          borderColor: selected ? 'primary.main' : 'transparent',
          transition: 'border-color 120ms, background-color 120ms',
          position: 'relative',
          cursor: 'pointer',
          '&:hover': { backgroundColor: selected ? 'action.selected' : 'action.hover' },
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      >
        <Box sx={{ p: 1 }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              backgroundColor: 'rgba(0,0,0,0.4)',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {agent.isOnline ? (
              <img
                src={thumbnailSrc}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <ComputerIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            )}

            {/* Countdown progress până la următorul thumbnail (interval per-agent) */}
            {agent.isOnline ? (
              <Tooltip title={`Next thumbnail in ${remainingSec}s (interval ${agent.thumbnailIntervalSeconds}s)`}>
                <Box sx={{ position: 'absolute', bottom: 4, left: 4, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.55)', borderRadius: '50%' }}>
                  <CircularProgress
                    variant="determinate"
                    value={progressPct}
                    size={20}
                    thickness={5}
                    sx={{ color: 'primary.light' }}
                  />
                </Box>
              </Tooltip>
            ) : null}

            {/* Bulk select checkbox — vizibil la hover sau dacă bifat */}
            {(hovered || bulkSelected) ? (
              <Checkbox
                checked={bulkSelected}
                onClick={(e) => { e.stopPropagation(); onToggleBulk(); }}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 2,
                  left: 2,
                  p: 0.25,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  borderRadius: 1,
                  color: 'common.white',
                  '&.Mui-checked': { color: 'primary.light' },
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                }}
              />
            ) : null}

            {/* Status dot */}
            <Box
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: agent.isOnline ? 'success.main' : 'error.main',
                boxShadow: agent.isOnline ? '0 0 6px rgba(16,185,129,0.6)' : undefined,
              }}
            />

            {/* Activity indicator pentru streams active */}
            {agent.activeStreamsCount > 0 ? (
              <Badge
                badgeContent={`REC ${agent.activeStreamsCount}`}
                color="error"
                sx={{
                  position: 'absolute',
                  top: 24,
                  right: 24,
                  '& .MuiBadge-badge': { fontSize: 9, height: 16, animation: 'argusPulse 1.5s infinite' },
                }}
              />
            ) : null}

            {/* Quick actions hover */}
            {hovered && agent.isOnline ? (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  position: 'absolute',
                  bottom: 6,
                  right: 6,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  borderRadius: 1,
                  px: 0.5,
                }}
              >
                <Tooltip title="Quick screenshot">
                  <IconButton
                    size="small"
                    sx={{ color: 'common.white', p: 0.5 }}
                    onClick={(e) => { e.stopPropagation(); onQuickScreenshot(); }}
                  >
                    <PhotoCameraIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="File Explorer">
                  <IconButton
                    size="small"
                    sx={{ color: 'common.white', p: 0.5 }}
                    onClick={(e) => { e.stopPropagation(); onQuickFileExplorer(); }}
                  >
                    <FolderOpenIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Livestream">
                  <IconButton
                    size="small"
                    sx={{ color: 'common.white', p: 0.5 }}
                    onClick={(e) => { e.stopPropagation(); onQuickLivestream(); }}
                  >
                    <VideocamIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : null}
          </Box>

          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
              {machineLabel}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25, gap: 1 }}>
              <Typography variant="caption" sx={{ color: agent.isOnline ? 'success.main' : 'text.disabled', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {relativeText}
              </Typography>
              {versionStatus.data && (
                <VersionLink
                  status={
                    agent.isOnline && agent.agentVersion
                      ? { ...versionStatus.data, currentVersion: agent.agentVersion }
                      : versionStatus.data
                  }
                  onClick={() => setVersionModalOpen(true)}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    </Tooltip>
    <AgentVersionModal
      agentId={agent.id}
      pcName={agent.pcName || agent.hostname}
      open={versionModalOpen}
      onClose={() => setVersionModalOpen(false)}
    />
    </>
  );
}
